// Accounts and credentials (ADR 0041): the provider accounts the platform
// uses to text people, send email, take M-Pesa payments and check identities.
// A change is a request until a second administrator approves it; secrets are
// write-only and never shown again. An account set here takes precedence over
// the server's configuration within half a minute.
import {
  Badge,
  Button,
  Group,
  Loader,
  NativeSelect,
  Paper,
  PasswordInput,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useStepUp } from "../../shared/session/stepup";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { AdminLayout } from "./AdminLayout";
import { APPROVALS_KEY, Requested, useRequested } from "./approvals";

type Account = Schemas["ProviderAccount"];
type Provider = Account["provider"];
type Kind = Schemas["ProviderUpdate"]["kind"];

type Field =
  | { name: string; type: "text"; required?: boolean; placeholder?: string }
  | { name: string; type: "switch" }
  | { name: string; type: "select"; options: string[] }
  | { name: string; type: "secret"; required?: boolean };

type Spec = { provider: Provider; kind: Kind; fields: Field[]; testable: boolean };

// The same fields the server checks (backend governance/domain).
const SPECS: Spec[] = [
  {
    provider: "sms",
    kind: "africastalking",
    testable: true,
    fields: [
      { name: "username", type: "text", required: true },
      { name: "sender_id", type: "text", placeholder: "none" },
      { name: "sandbox", type: "switch" },
      { name: "api_key", type: "secret", required: true },
    ],
  },
  {
    provider: "email",
    kind: "smtp",
    testable: false,
    fields: [
      { name: "host", type: "text", required: true, placeholder: "smtp.example.com" },
      { name: "port", type: "text", placeholder: "587" },
      { name: "username", type: "text" },
      { name: "implicit_tls", type: "switch" },
      { name: "password", type: "secret" },
    ],
  },
  {
    provider: "mpesa",
    kind: "daraja",
    testable: true,
    fields: [
      { name: "environment", type: "select", options: ["sandbox", "production"] },
      { name: "shortcode", type: "text", required: true },
      { name: "consumer_key", type: "secret", required: true },
      { name: "consumer_secret", type: "secret", required: true },
      { name: "passkey", type: "secret", required: true },
    ],
  },
  {
    provider: "kyc",
    kind: "generic_kyc_http",
    testable: false,
    fields: [
      { name: "url", type: "text", required: true, placeholder: "https://" },
      { name: "auth_header", type: "text", placeholder: "Authorization" },
      { name: "auth_value", type: "secret", required: true },
    ],
  },
];

const PROVIDERS_KEY = ["admin", "providers"] as const;

export function AdminProvidersPage() {
  const { t } = useTranslation();
  const accounts = useQuery({ queryKey: PROVIDERS_KEY, queryFn: () => unwrap(api.GET("/admin/providers")) });
  const approvals = useQuery({ queryKey: APPROVALS_KEY, queryFn: () => unwrap(api.GET("/admin/approvals")) });
  const waiting = new Set(
    (approvals.data?.items ?? [])
      .filter((a) => a.action === "provider_settings" && a.status === "pending")
      .map((a) => a.target),
  );
  return (
    <AdminLayout title={t("admin.title")} intro={t("providers.intro")}>
      {accounts.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : accounts.isError ? (
        <FormError message={errorMessage(accounts.error, t("error.generic"))} />
      ) : (
        <Stack gap="lg">
          {SPECS.map((spec) => (
            <ProviderCard
              key={spec.provider}
              spec={spec}
              account={accounts.data.items.find((a) => a.provider === spec.provider)}
              waiting={waiting.has(spec.provider)}
            />
          ))}
          <ServerAccounts />
        </Stack>
      )}
    </AdminLayout>
  );
}

function initial(spec: Spec, account: Account | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of spec.fields) {
    if (f.type === "secret") continue;
    out[f.name] =
      account?.settings[f.name] ?? (f.type === "switch" ? "false" : f.type === "select" ? (f.options[0] ?? "") : "");
  }
  return out;
}

function ProviderCard({ spec, account, waiting }: { spec: Spec; account: Account | undefined; waiting: boolean }) {
  const { t } = useTranslation();
  const f = useFormat();
  const stepUp = useStepUp();
  const requested = useRequested();
  const p = spec.provider;
  const [settings, setSettings] = useState(() => initial(spec, account));
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<{ ok: boolean; detail: string } | null>(null);
  const label = (name: string) => t(`providers.field.${name}`);
  const hint = (name: string) => t(`providers.hint.${name}`, { defaultValue: "" });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSent(false);
    const token = await stepUp("admin_change");
    if (!token) return;
    setBusy(true);
    const body = {
      kind: spec.kind,
      settings: Object.fromEntries(Object.entries(settings).filter(([, v]) => v !== "")),
      secrets: Object.fromEntries(Object.entries(secrets).filter(([, v]) => v !== "")),
    };
    const { response, error } = await api.PUT("/admin/providers/{provider}", {
      params: { path: { provider: p }, header: { "X-Step-Up-Token": token } },
      body,
    });
    setBusy(false);
    if (response.status !== 202) return setError(errorMessage(error, t("error.generic")));
    setSecrets({});
    setSent(true);
    requested();
  };

  const runTest = async () => {
    setTest(null);
    setTesting(true);
    const { data, error } = await api.POST("/admin/providers/{provider}/test", { params: { path: { provider: p } } });
    setTesting(false);
    setTest(data ?? { ok: false, detail: errorMessage(error, t("error.generic")) });
  };

  return (
    <Paper withBorder p="md" radius="md" data-testid={`provider-${p}`}>
      <form onSubmit={(e) => void save(e)} noValidate>
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap">
            <Title order={2} size="h4">
              {t(`providers.${p}.title`)}
            </Title>
            <Group gap="xs">
              {waiting && (
                <Badge color="orange" variant="light">
                  {t("providers.waiting")}
                </Badge>
              )}
              <Badge color={account ? "green" : "gray"} variant="light">
                {account ? t("providers.set_here") : t("providers.server")}
              </Badge>
            </Group>
          </Group>
          <Text size="sm">{t(`providers.${p}.intro`)}</Text>
          {account && (
            <Text size="xs" c="dimmed">
              {t("providers.version", { version: account.version, date: f.dateTime(account.updated_at) })}
            </Text>
          )}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {spec.fields.map((fl) => {
              if (fl.type === "switch")
                return (
                  <Switch
                    key={fl.name}
                    label={label(fl.name)}
                    description={hint(fl.name)}
                    checked={settings[fl.name] === "true"}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, [fl.name]: e.currentTarget.checked ? "true" : "false" }))
                    }
                  />
                );
              if (fl.type === "select")
                return (
                  <NativeSelect
                    key={fl.name}
                    label={label(fl.name)}
                    description={hint(fl.name)}
                    value={settings[fl.name]}
                    onChange={(e) => setSettings((s) => ({ ...s, [fl.name]: e.currentTarget.value }))}
                    data={fl.options.map((o) => ({ value: o, label: t(`providers.option.${o}`) }))}
                  />
                );
              if (fl.type === "secret") {
                const set = account?.secrets_set.includes(fl.name) ?? false;
                return (
                  <PasswordInput
                    key={fl.name}
                    label={label(fl.name)}
                    description={set ? t("providers.secret_set") : hint(fl.name)}
                    placeholder={set ? "••••••••" : ""}
                    autoComplete="new-password"
                    value={secrets[fl.name] ?? ""}
                    required={fl.required === true && !set}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setSecrets((s) => ({ ...s, [fl.name]: v }));
                    }}
                  />
                );
              }
              return (
                <TextInput
                  key={fl.name}
                  label={label(fl.name)}
                  description={hint(fl.name)}
                  placeholder={fl.placeholder ?? ""}
                  required={fl.required === true}
                  value={settings[fl.name] ?? ""}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setSettings((s) => ({ ...s, [fl.name]: v }));
                  }}
                />
              );
            })}
          </SimpleGrid>
          {sent && <Requested />}
          <FormError message={error} />
          {test && (test.ok ? <Notice>{test.detail}</Notice> : <FormError message={test.detail} />)}
          <Group gap="xs">
            <Button type="submit" loading={busy} disabled={waiting}>
              {t("providers.save")}
            </Button>
            {spec.testable && (
              <Button variant="default" loading={testing} onClick={() => void runTest()}>
                {t(`providers.${p}.test`)}
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Paper>
  );
}

/** ServerAccounts lists what stays in the server's configuration. */
function ServerAccounts() {
  const { t } = useTranslation();
  return (
    <Paper withBorder p="md" radius="md" data-testid="server-accounts">
      <Stack gap="xs">
        <Title order={2} size="h4">
          {t("providers.server_title")}
        </Title>
        <Text size="sm">{t("providers.server_intro")}</Text>
        {(["ussd", "callbacks", "domains", "keys"] as const).map((k) => (
          <Text key={k} size="sm">
            <Text span fw={600}>
              {t(`providers.server_item.${k}.title`)}
            </Text>
            {": "}
            {t(`providers.server_item.${k}.body`)}
          </Text>
        ))}
      </Stack>
    </Paper>
  );
}
